import { BaseSelect } from '@gasbi/shared/common/components/BaseStyledComponents';
import { FormField } from '@gasbi/shared/common/components/FormField';
import {
  EICSymbolField,
  InputField,
  SelectFieldDynamic,
  TextareaField,
} from '@gasbi/shared/common/components/FormFields';
import { FormSection } from '@gasbi/shared/web/components/FormSection';

import { useGetOrganizationByIdQuery } from '@/features/organizations/organizationsApi';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/joy/Checkbox';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import Option from '@mui/joy/Option';
import Stack from '@mui/joy/Stack';

import { CreateSubdivisionStepProps } from '../types';
import { useParentSubdivisionOptions } from '../useParentSubdivisionOptions';

// As the ГРМ card: one «Загальна інформація» section when creating, a section per group when editing.
const GeneralInfoStep = ({
  formik,
  isEditMode,
}: CreateSubdivisionStepProps) => {
  const orgId: string | undefined = formik.values.headOrganizationId;
  const { data: organization } = useGetOrganizationByIdQuery(orgId ?? '', {
    skip: !orgId,
  });
  const { options, isLoading, isError, hasNextPage, fetchNextPage } =
    useParentSubdivisionOptions(
      orgId,
      formik.values.id,
      formik.initialValues.parentId,
    );

  const placement = (
    <>
      <FormField
        control={formik}
        name='headOrganizationId'
        label='Організація'
        required
        preview
        customPreviewRender
        disabled
      >
        {() => (
          <BaseSelect
            value={organization?.id ?? null}
            disabled
            indicator={<ArrowDropDownIcon fontSize='small' />}
          >
            {(organization ? [organization] : []).map(o => (
              <Option key={o.id} value={o.id}>
                {o.name}
              </Option>
            ))}
          </BaseSelect>
        )}
      </FormField>
      <SelectFieldDynamic
        control={formik}
        name='parentId'
        label='Батьківський підрозділ'
        placeholder='Оберіть зі списку'
        options={options}
        isOptionsDataLoading={isLoading}
        disabled={isError}
        errorMessage={
          isError
            ? 'Не вдалося завантажити підрозділи, батьківський підрозділ не можна змінити'
            : undefined
        }
        hasNextPage={hasNextPage}
        onLoadMore={fetchNextPage}
        endDecorator={
          !!formik.values.parentId && (
            <IconButton
              sx={{ p: 0, minWidth: 'auto', minHeight: 'auto', mr: 1 }}
              size='sm'
              onClick={() => formik.setFieldValue('parentId', null)}
            >
              <CloseIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
          )
        }
      />
    </>
  );

  const naming = (
    <>
      <InputField control={formik} name='name' label='Найменування' required />
      <TextareaField
        control={formik}
        name='fullName'
        label='Повне найменування'
        required
      />
    </>
  );

  const identifiers = (
    <EICSymbolField
      control={formik}
      maxLength={1}
      placeholder='A'
      mask={null}
    />
  );

  const isActive = (
    <FormField control={formik} name='isActive'>
      {({ name, value, onChange, onBlur }) => (
        <Checkbox
          name={name}
          label='Активний підрозділ'
          color='neutral'
          variant='outlined'
          size='sm'
          checked={value}
          onChange={onChange}
          onBlur={onBlur}
        />
      )}
    </FormField>
  );

  if (isEditMode) {
    return (
      <Stack divider={<Divider />} gap={2}>
        <FormSection title='Організація'>{placement}</FormSection>
        <FormSection title='Найменування'>{naming}</FormSection>
        <FormSection title='Ідентифікатори'>{identifiers}</FormSection>
        <FormSection title='Додатково'>{isActive}</FormSection>
      </Stack>
    );
  }

  return (
    <FormSection title='Загальна інформація'>
      {placement}
      {naming}
      {identifiers}
      {isActive}
    </FormSection>
  );
};

export default GeneralInfoStep;
